import {
  AppConfig,
  useSubscriptionAccessConfigContext,
} from '@ltvco/refresh-lib/ctx';
import {
  usePhoneSearchData,
  useReport,
  useCreateRemouladeReportSnapshot,
  useReportMonitors,
} from '@ltvco/refresh-lib/hooks';
import { Box } from '@ltvco/refresh-lib/theme';
import {
  isZeroed,
  useScrollToSectionOnNavigate,
  unformatPhone,
  isNeighborSpoofed,
  formatPhone,
  DateUtil,
} from '@ltvco/refresh-lib/utils';
import {
  CommentsSection,
  commonTypes,
  DebugMenu,
  NeighborSpoofing,
  NuisanceCaller,
  PhoneFraudScanSection,
  PotentialSpam,
  ReportFactory,
  ReportLoading,
  ReportMonitor,
  ReportOptions,
  SearchVolume,
  useSession,
} from '@ltvco/refresh-lib/v1';
import {
  PhoneSection as PhoneSectionV2,
  EmailSection as EmailSectionV2,
  AddressHistorySection as AddressHistorySectionV2,
  SocialSection as SocialSectionV2,
  JobsSection as JobsSectionV2,
  EducationSection as EducationSectionV2,
  ReportRating as ReportRatingV2,
  RelatedReportsSection as RelatedReportsSectionV2,
  NotesSection as NotesSectionV2,
  AssetsSection as AssetsSectionV2,
  PotentialOwners as PotentialOwnersV2,
  PhoneReportOverview,
  ReportNullState,
  ReportClaimingSection,
  MonitoringCard,
  useRecentReports,
} from '@ltvco/refresh-lib/v2';
import { useFeatureIsOn, useQueryClient } from '@ltvco/refresh-lib/vendors';
import { useContext, useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { constants } from 'appConstants';
import { getEmulatedUseQueryResult } from 'components/ChatBV/utils';
import { isAfterTodaysCutoff } from 'utils/isAfterTodaysCutoff';

import legitCallerGifUrl from '../../../../../images/caller_legit.gif';
import questionableCallerGifUrl from '../../../../../images/caller_questionable.gif';
import telemarketerCallerGifUrl from '../../../../../images/caller_telemarketer.gif';

import type { ReportProps } from 'components/ChatBV/types';

interface PhoneReportProps extends ReportProps {}

const reportType = 'phone';

export function PhoneChatBVReport({ permalink, reportData }: PhoneReportProps) {
  const {
    session: { account },
  } = useSession();
  const { logError } = useContext(AppConfig);

  // TODO: all this monitored report logic needs a refactor.
  // There is no point in refetching a report when a user toggles monitoring, so we use the initial value.
  const { currentMonitors, reportIsMonitored } = useReportMonitors(permalink);
  const [neighborSpoofed, setIsNeighborSpoofed] = useState(false);
  const isInStaggeredPhoneUpdateVariation = useFeatureIsOn('OAR-1352');
  const isPhonePartialAddress = useFeatureIsOn('OAR-1706');
  const isInListV2Variation = useFeatureIsOn('prop-2834');
  const createRemouladeReportSnapshot = useCreateRemouladeReportSnapshot();
  const queryClient = useQueryClient();
  const { isHidden } = useSubscriptionAccessConfigContext();

  const [bbqReportIsReady, setIsBbqReportReady] = useState(false);
  const { data: bbqPhoneData } = useRecentReports(
    { report_type: 'reverse_phone_report' },
    { enabled: bbqReportIsReady }
  );
  const phoneReports = useMemo(
    () => bbqPhoneData?.reports ?? [],
    [bbqPhoneData?.reports]
  );

  const apiQueryResult = useReport(permalink, reportIsMonitored, 'phone', {
    enabled: !reportData,
  });
  const queryResult = reportData
    ? getEmulatedUseQueryResult(reportData, true, false, false)
    : apiQueryResult;

  const currentUserInfo = account?.account?.user_info;

  const zeroed = isZeroed(queryResult as any);
  const { hash, search } = useLocation();
  useScrollToSectionOnNavigate(hash, queryResult as any);

  const [reportOptions, setReportOptions] = useState<ReportOptions>({
    potentialOwnerIndex: 0,
    showHighConfidenceDataOnly: false,
    higherConfidenceThreshold: constants.config.higherConfidenceThreshold,
  });
  const handleSetReportOptions = (newOptions: Partial<ReportOptions>) => {
    setReportOptions({ ...reportOptions, ...newOptions });
  };
  const [activeClaiming, setActiveClaiming] = useState(false);
  const {
    data: phoneSearchData,
    error,
    isError,
  } = usePhoneSearchData(queryResult?.data?.meta?.search_data?.phone_number);
  useEffect(() => {
    if (isError && error) {
      const msg = error.error || error.message || '';
      if (!msg.toLowerCase().includes('not found')) {
        logError('Error fetching phone search data', error);
      }
    }
  }, [isError, error, logError]);

  useEffect(() => {
    setActiveClaiming(
      currentMonitors?.some((monitor: ReportMonitor) => {
        return monitor.permalink === permalink && monitor.is_claimed;
      })
    );
  }, [currentMonitors, permalink]);

  const [reportUpdateDate, setUpdateDate] = useState<string | undefined>();
  const date = new DateUtil();

  const report = useMemo(() => {
    if (!queryResult.isSuccess || !queryResult.data) {
      return null;
    }

    if (queryResult?.data?.entities) {
      return ReportFactory.create(queryResult?.data as any, 'phone');
    }
  }, [queryResult.data, queryResult.isSuccess]);

  const loading = useMemo(() => {
    return queryResult.isLoading || queryResult.isError;
  }, [queryResult.isLoading, queryResult.isError]);

  useEffect(() => {
    if (queryResult.isSuccess && queryResult.data) {
      queryClient.invalidateQueries(['request', 'bbq']);

      // After a 3 second delay, we expect the report that was just run to be in BBQ
      const timer = setTimeout(() => {
        queryClient.invalidateQueries(['request', 'bbq']);
        setIsBbqReportReady(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [queryResult.data, queryResult.isSuccess]);

  const hidePersonSection =
    isInStaggeredPhoneUpdateVariation &&
    isAfterTodaysCutoff(currentUserInfo?.join_date) &&
    phoneReports.length == 1;

  useEffect(() => {
    // Only run the request to remoulade if this report is the user's only phone report, or "first" report
    if (
      !hidePersonSection ||
      phoneReports.length != 1 ||
      report?.data?.people?.length === 0
    )
      return;
    createRemouladeReportSnapshot.mutate({
      permalink,
      update_type: 'phone',
    });
  }, [hidePersonSection, phoneReports]);

  useEffect(() => {
    const remoUpdateDate = currentMonitors?.find(
      (monitor) => monitor.permalink === permalink
    )?.updated_at;
    const coreUpdateDate = report?.data?.meta?.updated_at;

    if (reportIsMonitored && remoUpdateDate)
      setUpdateDate(date.parseDateFromString(remoUpdateDate, 'yyyy-MM-dd'));
    else if (coreUpdateDate)
      setUpdateDate(
        date.parseDateFromString(
          coreUpdateDate,
          'yyyy-MM-dd',
          'yyyy-MM-dd HH:mm:ss ZZZ'
        )
      );

    if (report) {
      const spoofed = isNeighborSpoofed(
        report?.data,
        currentUserInfo?.phone_number
      );
      setIsNeighborSpoofed(spoofed);
    }
  }, [
    report?.data?.meta?.updated_at,
    currentMonitors,
    permalink,
    reportIsMonitored,
  ]);

  if (loading || queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={15} />;
  }
  if (zeroed || (queryResult.isSuccess && !report)) {
    return <ReportNullState />;
  }

  const showReportClaiming =
    unformatPhone(currentUserInfo?.phone_number || '') === report?.phone;

  const ClaimingComponent = showReportClaiming && (
    <ReportClaimingSection
      reportPermaLink={permalink}
      reportType={reportType}
      reportIsMonitoredAndClaimed={activeClaiming}
    />
  );

  const MonitoringComponent = (
    <MonitoringCard permalink={permalink} reportType={reportType} />
  );

  if (!report) return <ReportLoading menuItems={15} />;

  const businesses =
    report?.data?.businesses ??
    report?.data?.rawData?.entities?.businesses ??
    [];
  const businessName = businesses[0]?.names?.[0]?.name ?? '';

  const isBusinessSelected =
    reportOptions.potentialOwnerIndex < businesses.length;
  const selectedBusinessName = isBusinessSelected
    ? businesses[reportOptions.potentialOwnerIndex]?.names?.[0]?.name ?? ''
    : '';

  const personOwnerOptions = {
    ...reportOptions,
    potentialOwnerIndex: isBusinessSelected
      ? 0
      : reportOptions.potentialOwnerIndex - businesses.length,
  };
  const owner = report.getOwner(personOwnerOptions);

  const {
    ownerName,
    emails,
    educations,
    jobs,
    profiles,
    usernames,
    phones,
    addresses,
    ownedAssets,
    relatives,
  } = owner;

  const { risk, connectivity, is_valid } = report.supportingPhone || {};
  const {
    fraud_score,
    is_prepaid,
    is_voip,
    is_risky,
    is_recent_abuse,
    is_do_not_call,
  } = risk || {};

  const is_active = connectivity?.is_active;

  const showPhoneFraudScan =
    report.supportingPhone != null && risk != null && connectivity != null;

  const queryParams = new URLSearchParams(search);
  const fallbackPhone = queryParams.get('phone') || '';
  const phoneNumber = formatPhone(report.phone) ?? formatPhone(fallbackPhone);

  const otherPhones = phones.filter(
    (phone: commonTypes.Phone) =>
      phone.number !== fallbackPhone && phone.number !== report.phone
  );

  const selectedOwnerDisplayName = isBusinessSelected
    ? selectedBusinessName
    : ownerName || report.data.formatted_number;

  let potentialOwnersNumber =
    businesses.length + (report.data.people?.length ?? 0);
  if (potentialOwnersNumber === 0 && businessName) potentialOwnersNumber = 1;
  if (isInListV2Variation) potentialOwnersNumber = 1;

  const { main_line_type, num_searches, spam_score } = report.data;
  const searchCount = phoneSearchData?.total_volume ?? num_searches ?? 0;

  const comments = report.data.comments || [];
  const carrier = report.data.meta?.search_data?.carrier;

  document.title = `${formatPhone(phoneNumber)} - BeenVerified`;

  const potentialOwners = report.data.people;

  const alternativeTitle = isPhonePartialAddress
    ? 'Possible Locations'
    : undefined;

  return (
    <Box>
      <Box marginTop={0}>
        {potentialOwnersNumber === 0 && <PotentialSpam />}

        <PhoneReportOverview
          dateUpdated={reportUpdateDate}
          reportType={reportType}
          phone={formatPhone(phoneNumber)}
          personName={selectedOwnerDisplayName}
          neighborSpoofed={neighborSpoofed}
          phoneOverviewItemsData={{
            comments,
            mainLineType: main_line_type,
            numSearches: searchCount,
            spamScore: spam_score,
            carrier,
            peopleCount: potentialOwnersNumber,
          }}
          Monitoring={MonitoringComponent}
          showHighConfidenceToggle={false}
          showHighConfidenceDataOnly={reportOptions.showHighConfidenceDataOnly}
          setShowHighConfidenceDataOnly={(value: boolean) => {
            handleSetReportOptions({ showHighConfidenceDataOnly: value });
          }}
        />

        {ClaimingComponent}

        <PotentialOwnersV2
          potentialOwners={potentialOwners || []}
          potentialOwnerIndex={reportOptions.potentialOwnerIndex}
          setPotentialOwnerIndex={(index: number) => {
            handleSetReportOptions({ potentialOwnerIndex: index });
          }}
          personName={selectedOwnerDisplayName}
          permalink={permalink}
          businesses={businesses}
          showHighConfidenceDataOnly={reportOptions.showHighConfidenceDataOnly}
          setShowHighConfidenceDataOnly={(value: boolean) => {
            handleSetReportOptions({
              showHighConfidenceDataOnly: value,
            });
          }}
          hidePersonSection={hidePersonSection}
          reportType={reportType}
        />

        <PhoneSectionV2
          personName={selectedOwnerDisplayName}
          phoneList={otherPhones}
          permalink={permalink}
          alternateTitle="Other Phone Numbers"
        />
        <EmailSectionV2
          personName={selectedOwnerDisplayName}
          emailList={emails}
          permalink={permalink}
        />
        <AddressHistorySectionV2
          addresses={addresses}
          personName={selectedOwnerDisplayName}
          permalink={permalink}
          realEstate={ownedAssets?.real_estate}
          alternateTitle={alternativeTitle}
        />
        <SocialSectionV2
          personName={selectedOwnerDisplayName}
          profiles={profiles}
          usernames={usernames}
          permalink={permalink}
        />
        <JobsSectionV2
          personName={selectedOwnerDisplayName}
          jobs={jobs}
          permalink={permalink}
        />
        <EducationSectionV2
          personName={selectedOwnerDisplayName}
          educations={educations}
          permalink={permalink}
        />
        <AssetsSectionV2
          personName={selectedOwnerDisplayName}
          ownedAssets={ownedAssets}
          permalink={permalink}
        />

        <PhoneFraudScanSection
          fraudScore={fraud_score}
          validStatus={is_valid}
          prepaid={is_prepaid}
          voip={is_voip}
          risky={is_risky}
          recentAbuse={is_recent_abuse}
          active={is_active}
          doNotCall={is_do_not_call}
          phoneNumber={phoneNumber}
          showPhoneFraudScan={showPhoneFraudScan}
          showReportCTA={!isHidden('fraud_scan')}
        />
        <NeighborSpoofing phoneReportData={report.data} />
        <SearchVolume searchCount={searchCount} />
        <NuisanceCaller
          reportData={report.data}
          callerSourceMap={{
            telemarketer: telemarketerCallerGifUrl,
            questionable: questionableCallerGifUrl,
            legit: legitCallerGifUrl,
          }}
        />
        <CommentsSection
          reportData={report.data}
          permalink={permalink}
          reportType={reportType}
        />

        <NotesSectionV2 permalink={permalink} />
        <RelatedReportsSectionV2
          reportType="phone"
          personName={selectedOwnerDisplayName}
          potentialOwners={report.data.people}
          relatives={relatives}
          addresses={addresses}
          phones={phones}
          emails={emails}
          usernames={usernames}
          hidePersonSection={hidePersonSection}
        />
        <ReportRatingV2
          rating={report.data.meta?.rating || null}
          report_type={reportType}
        />
      </Box>
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
    </Box>
  );
}
